import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios, { setCsrfToken } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const clearSession = () => {
    setCsrfToken(null);
    setUser(null);
  };

  useEffect(() => {
    let active = true;
    axios.get("/api/auth/session", { withCsrf: false })
      .then((response) => {
        if (!active) return;
        setUser(response.data.user);
        setCsrfToken(response.data.csrfToken);
      })
      .catch(() => {
        if (active) clearSession();
      })
      .finally(() => {
        if (active) setAuthLoading(false);
      });

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401) clearSession();
        return Promise.reject(error);
      }
    );
    return () => {
      active = false;
      axios.interceptors.response.eject(interceptorId);
    };
  }, []);

  const login = async (email, password) => {
    setAuthLoading(true);
    try {
      const res = await axios.post("/api/login", { email, password }, { withCsrf: false });
      if (!res.data?.user || !res.data?.csrfToken) {
        throw new Error("Session response missing required fields");
      }
      setCsrfToken(res.data.csrfToken);
      setUser(res.data.user);
      return res.data;
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post("/api/logout");
    } finally {
      clearSession();
    }
  };

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      login,
      logout,
      authLoading,
    }),
    [user, authLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
